import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import arabicFontUrl from '../assets/fonts/NotoSansArabic-Regular.ttf';

const fmt = (d) => {
  if (!d) return '\u2014';
  try { return new Date(d).toLocaleDateString('en-US'); }
  catch { return String(d); }
};

const hexToRgb = (hex = '#BC1F41') => {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [parseInt(f.slice(0, 2), 16), parseInt(f.slice(2, 4), 16), parseInt(f.slice(4, 6), 16)];
};

const lightenRgb = (hex, factor = 0.88) => {
  const [r, g, b] = hexToRgb(hex);
  return [
    Math.round(r + (255 - r) * factor),
    Math.round(g + (255 - g) * factor),
    Math.round(b + (255 - b) * factor),
  ];
};

const loadArabicFont = async () => {
  const res = await fetch(arabicFontUrl);
  const buf = await res.arrayBuffer();
  return btoa(new Uint8Array(buf).reduce((d, b) => d + String.fromCharCode(b), ''));
};

const loadLogo = async () => {
  try {
    const res = await fetch('/attachments/Logos/logo.png');
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    return 'data:image/png;base64,' + btoa(new Uint8Array(buf).reduce((d, b) => d + String.fromCharCode(b), ''));
  } catch {
    return null;
  }
};

export const generateOrderPDF = async ({
  details,
  result,
  attachments = [],
  configLinks = {},
  generalServices = {},
  certificateData = null,
  isRTL = true,
  statusText = '',
  pdfTitle = null,
}) => {
  const primaryHex = configLinks?.primaryColor || '#BC1F41';
  const [pr, pg, pb] = hexToRgb(primaryHex);
  // Tint = very light version of primary for label column background
  const tint = lightenRgb(primaryHex, 0.88);

  const munName = isRTL
    ? (configLinks?.municipalityNameAr?.trim() || '')
    : (configLinks?.municipalityNameEn?.trim() || '');

  const d = details;
  const lbl = (ar, en) => (isRTL ? ar : en);

  const requestNumber = d?.subscriberServiceFullNumber || result?.requestNumber || '';
  const serviceName   = d?.serviceType?.name           || result?.serviceName   || '';
  const insertDate    = fmt(d?.insertDate || result?.insertDate);
  const fee           = d?.applicationFee ?? result?.applicationFee;
  const currency      = d?.currencyCode   || result?.currencyChar3Code || '';
  const serviceGoal   = d?.serviceGoal || '';
  const reviewNote    = d?.serviceReviewNote || '';
  const beneficiary   = d?.beneficiaryType?.name || '';
  const location      = d?.isValidLocation && d?.location ? d.location : '';
  const subCode       = d?.citizenService?.code || '';
  const stageName     = result?.stateName || '';
  const activityName  = d?.activityName || result?.activityName || '';
  const fragmentName  = d?.fragmentName?.trim().toLowerCase() || '';
  const printDate     = new Date().toLocaleDateString('en-US');

  const [fontBase64, logoDataUrl] = await Promise.all([loadArabicFont(), loadLogo()]);

  const doc = new jsPDF('p', 'mm', 'a4');
  doc.addFileToVFS('NotoSansArabic-Regular.ttf', fontBase64);
  doc.addFont('NotoSansArabic-Regular.ttf', 'NotoArabic', 'normal');
  doc.setFont('NotoArabic');

  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginL  = 14;
  const marginR  = 14;
  const tableW   = pageWidth - marginL - marginR;
  const radius   = 3;    // rounded corner radius for sections
  const headerH  = 8.5; // section header bar height
  const labelColW = 50;
  let y = 10;

  // =========================================================
  // HEADER CARD  — 3-column layout: date | logo (center) | name
  // =========================================================
  const cardH   = 26;
  const logoSize = 18; // logo width & height in mm
  const logoPad  = (cardH - logoSize) / 2;
  const logoX    = (pageWidth - logoSize) / 2; // horizontally centred on page

  doc.setFillColor(250, 250, 252);
  doc.setDrawColor(pr, pg, pb);
  doc.setLineWidth(0.4);
  doc.roundedRect(marginL, y, tableW, cardH, radius, radius, 'FD');

  // ---- Center: logo ----
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', logoX, y + logoPad, logoSize, logoSize);
  }

  // ---- Right: municipality name + subtitle ----
  doc.setFontSize(13);
  doc.setTextColor(pr, pg, pb);
  doc.text(munName, pageWidth - marginR - 5, y + 10, { align: 'right' });

  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 130);
  doc.text(lbl('\u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a\u0629', 'E-Municipality Services'), pageWidth - marginR - 5, y + 17, { align: 'right' });

  // ---- Left: print date ----
  doc.setFontSize(7.5);
  doc.setTextColor(150, 150, 158);
  doc.text(printDate, marginL + 5, y + 10, { align: 'left' });

  y += cardH + 6;

  // =========================================================
  // TITLE BLOCK
  // =========================================================
  const titleText = pdfTitle || lbl('\u062a\u0641\u0627\u0635\u064a\u0644 \u0627\u0644\u0637\u0644\u0628', 'Request Details');
  doc.setFontSize(12);
  doc.setTextColor(pr, pg, pb);
  doc.text(titleText, pageWidth / 2, y, { align: 'center' });
  y += 7;

  doc.setFontSize(18);
  doc.setTextColor(30, 30, 30);
  doc.text(requestNumber, pageWidth / 2, y, { align: 'center' });
  y += 7;

  // Status badge pill
  const badgeText = statusText || '\u2014';
  doc.setFontSize(9);
  const bw = Math.max(45, doc.getTextWidth(badgeText) + 20);
  const bh = 7;
  const bx = (pageWidth - bw) / 2;
  doc.setFillColor(tint[0], tint[1], tint[2]);
  doc.setDrawColor(pr, pg, pb);
  doc.setLineWidth(0.5);
  doc.roundedRect(bx, y - 4.5, bw, bh, bh / 2, bh / 2, 'FD');
  doc.setTextColor(pr, pg, pb);
  doc.text(badgeText, pageWidth / 2, y, { align: 'center' });
  y += 10;

  doc.setDrawColor(220, 220, 225);
  doc.setLineWidth(0.3);
  doc.line(marginL + 10, y, pageWidth - marginR - 10, y);
  y += 6;

  // =========================================================
  // HELPERS
  // =========================================================

  /**
   * Draw a section block with rounded corners.
   *
   * Columns:
   *   Arabic  (isRTL) → [value (center), label (center+tint)]  => label on RIGHT
   *   English (!isRTL) → [label (center+tint), value (center)] => label on LEFT
   */
  const drawSection = (icon, title, rows) => {
    const filtered = rows.filter((r) => r[1] != null && String(r[1]).trim() !== '');
    if (!filtered.length) return;

    const sectionStartY = y;

    // Header: rounded top, flat bottom (so it connects to table rows)
    doc.setFillColor(pr, pg, pb);
    doc.roundedRect(marginL, y, tableW, headerH, radius, radius, 'F');
    doc.rect(marginL, y + headerH / 2, tableW, headerH / 2, 'F');

    doc.setFontSize(9.5);
    doc.setTextColor(255, 255, 255);
    doc.text(icon, marginL + 5, y + 5.8, { align: 'left' });
    doc.text(title, pageWidth - marginR - 5, y + 5.8, { align: 'right' });
    y += headerH;

    let tableRows;
    let colStyles;
    if (isRTL) {
      tableRows = filtered.map((r) => [String(r[1]), r[0]]);
      colStyles = {
        0: { cellWidth: 'auto',    halign: 'center', textColor: [35, 35, 40] },
        1: { cellWidth: labelColW, halign: 'center', fillColor: tint, textColor: [pr, pg, pb] },
      };
    } else {
      tableRows = filtered.map((r) => [r[0], String(r[1])]);
      colStyles = {
        0: { cellWidth: labelColW, halign: 'center', fillColor: tint, textColor: [pr, pg, pb] },
        1: { cellWidth: 'auto',    halign: 'center', textColor: [35, 35, 40] },
      };
    }

    autoTable(doc, {
      startY: y,
      margin: { left: marginL, right: marginR },
      tableWidth: tableW,
      body: tableRows,
      styles: {
        font: 'NotoArabic',
        fontSize: 9,
        lineColor: [230, 230, 235],
        lineWidth: 0.2,
        halign: 'center',
        valign: 'middle',
        cellPadding: { top: 3, right: 6, bottom: 3, left: 6 },
        minCellHeight: 9,
        fillColor: [255, 255, 255],
      },
      columnStyles: colStyles,
      alternateRowStyles: { fillColor: [248, 249, 252] },
      tableLineWidth: 0,
      theme: 'plain',
    });

    const sectionEndY = doc.lastAutoTable.finalY;

    // Rounded border wrapping entire section
    doc.setDrawColor(pr, pg, pb);
    doc.setLineWidth(0.5);
    doc.roundedRect(marginL, sectionStartY, tableW, sectionEndY - sectionStartY, radius, radius, 'S');

    y = sectionEndY + 6;
  };



  // =========================================================
  // SECTION 1: Request Info (combined — all general request rows)
  // =========================================================
  const infoRows = [
    [lbl('\u0631\u0642\u0645 \u0627\u0644\u0637\u0644\u0628',             'Request No.'),       requestNumber],
    [lbl('\u0646\u0648\u0639 \u0627\u0644\u0637\u0644\u0628',             'Service Type'),      serviceName  ],
    [lbl('\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u0637\u0644\u0628', 'Date'),               insertDate   ],
    [lbl('\u0627\u0644\u062d\u0627\u0644\u0629',                          'Status'),             statusText   ],
    [lbl('\u0627\u0644\u063a\u0627\u064a\u0629 \u0645\u0646 \u0627\u0644\u0637\u0644\u0628',           'Service Goal'),      serviceGoal ],
    [lbl('\u0645\u0644\u0627\u062d\u0638\u0627\u062a \u0627\u0644\u0645\u0631\u0627\u062c\u0639\u0629', 'Review Notes'),      reviewNote  ],
    [lbl('\u0646\u0648\u0639 \u0627\u0644\u0645\u0633\u062a\u0641\u064a\u062f',                        'Beneficiary Type'),  beneficiary ],
    [lbl('\u0627\u0644\u0645\u0648\u0642\u0639',                                                        'Location'),          location    ],
    [lbl('\u0631\u0645\u0632 \u0627\u0644\u0627\u0634\u062a\u0631\u0627\u0643',                        'Subscription Code'), subCode     ],
  ];
  if (fee != null) {
    infoRows.splice(4, 0, [lbl('\u0627\u0644\u0631\u0633\u0648\u0645', 'Fee'), (String(fee) + ' ' + currency).trim()]);
  }
  drawSection('\u2756', lbl('\u0645\u0639\u0644\u0648\u0645\u0627\u062a \u0627\u0644\u0637\u0644\u0628', 'Request Info'), infoRows);

  // =========================================================
  // SECTION 2: Dynamic service details (only when fragment has data)
  //   Notes are included as a regular row — no separate block needed
  // =========================================================
  const serviceRows = [];
  if (fragmentName === 'generalcategories' && generalServices?.generalCategory?.name) {
    serviceRows.push([lbl('\u062a\u0635\u0646\u064a\u0641 \u0627\u0644\u0637\u0644\u0628', 'Category'), generalServices.generalCategory.name]);
  }
  if ((fragmentName === 'general' || fragmentName === 'generalcategories') && generalServices?.notes) {
    serviceRows.push([lbl('\u0627\u0644\u062a\u0641\u0627\u0635\u064a\u0644', 'Details'), generalServices.notes]);
  }
  if ((fragmentName === 'electricity' || fragmentName === 'water') && generalServices?.usePropertyType?.name) {
    serviceRows.push([lbl('\u0627\u0633\u062a\u062e\u062f\u0627\u0645 \u0627\u0644\u0639\u0642\u0627\u0631', 'Property Use'), generalServices.usePropertyType.name]);
  }
  if ((fragmentName === 'electricity' || fragmentName === 'water') && generalServices?.propertyDescription) {
    serviceRows.push([lbl('\u0648\u0635\u0641 \u0627\u0644\u0639\u0642\u0627\u0631', 'Property Desc.'), generalServices.propertyDescription]);
  }
  if (fragmentName === 'electricity' && generalServices?.electricityVoltageType?.name) {
    serviceRows.push([lbl('\u0646\u0648\u0639 \u0627\u0644\u062a\u064a\u0627\u0631 \u0627\u0644\u0643\u0647\u0631\u0628\u0627\u0626\u064a', 'Voltage Type'), generalServices.electricityVoltageType.name]);
  }
  if (fragmentName === 'servicewithdetails' && generalServices?.additionalData) {
    serviceRows.push([lbl('\u0627\u0644\u0639\u0646\u0648\u0627\u0646', 'Title'), generalServices.additionalData]);
  }
  if (fragmentName === 'servicewithdetails' && generalServices?.notes) {
    serviceRows.push([lbl('\u0627\u0644\u062a\u0641\u0627\u0635\u064a\u0644', 'Details'), generalServices.notes]);
  }
  if ((fragmentName === 'certificate' || fragmentName === 'clearancecertificate') && certificateData?.externalEntityName) {
    serviceRows.push([lbl('\u0627\u0644\u062c\u0647\u0629 \u0627\u0644\u0645\u0648\u062c\u0647 \u0625\u0644\u064a\u0647\u0627 \u0627\u0644\u0643\u062a\u0627\u0628', 'Addressee'), certificateData.externalEntityName]);
  }
  if (serviceRows.length) {
    drawSection('\u270e', lbl('\u062a\u0641\u0627\u0635\u064a\u0644 \u0627\u0644\u062e\u062f\u0645\u0629', 'Service Details'), serviceRows);
  }

  // =========================================================
  // SECTION 4: Request Path
  // =========================================================
  drawSection('\u27a4', lbl('\u0645\u0633\u0627\u0631 \u0627\u0644\u0637\u0644\u0628', 'Request Path'), [
    [lbl('\u0627\u0644\u0645\u0631\u062d\u0644\u0629 \u0627\u0644\u062d\u0627\u0644\u064a\u0629', 'Current Stage'),    stageName    || '\u2014'],
    [lbl('\u0627\u0644\u0625\u062c\u0631\u0627\u0621 \u0627\u0644\u062d\u0627\u0644\u064a',       'Current Activity'), activityName || '\u2014'],
  ]);

  // =========================================================
  // SECTION 5: Attachments
  // =========================================================
  if (attachments.length > 0) {
    const sectionStartY = y;

    doc.setFillColor(pr, pg, pb);
    doc.roundedRect(marginL, y, tableW, headerH, radius, radius, 'F');
    doc.rect(marginL, y + headerH / 2, tableW, headerH / 2, 'F');
    doc.setFontSize(9.5);
    doc.setTextColor(255, 255, 255);
    doc.text('\u2665', marginL + 5, y + 5.8, { align: 'left' });
    doc.text(lbl('\u0627\u0644\u0645\u0631\u0641\u0642\u0627\u062a', 'Attachments'), pageWidth - marginR - 5, y + 5.8, { align: 'right' });
    y += headerH;

    const attHead = isRTL
      ? [[lbl('\u0627\u0644\u062d\u0627\u0644\u0629', 'Status'), lbl('\u0646\u0648\u0639 \u0627\u0644\u0645\u0631\u0641\u0642', 'Attachment Type')]]
      : [[lbl('\u0646\u0648\u0639 \u0627\u0644\u0645\u0631\u0641\u0642', 'Attachment Type'), lbl('\u0627\u0644\u062d\u0627\u0644\u0629', 'Status')]];

    const attBody = attachments.map((a) => {
      const namePart   = String(a.attachmentTypeName || '') + (a.isMandatory ? ' *' : '');
      const statusPart = a.fileName ? lbl('\u2713 \u0645\u0631\u0641\u0642', '\u2713 Attached') : '\u2014';
      return isRTL ? [statusPart, namePart] : [namePart, statusPart];
    });

    const attColStyles = isRTL
      ? {
          0: { cellWidth: 38, halign: 'center', fillColor: tint, textColor: [pr, pg, pb] },
          1: { cellWidth: 'auto', halign: 'center', textColor: [35, 35, 40] },
        }
      : {
          0: { cellWidth: 'auto', halign: 'center', textColor: [35, 35, 40] },
          1: { cellWidth: 38, halign: 'center', fillColor: tint, textColor: [pr, pg, pb] },
        };

    autoTable(doc, {
      startY: y,
      margin: { left: marginL, right: marginR },
      tableWidth: tableW,
      head: attHead,
      body: attBody,
      styles: {
        font: 'NotoArabic',
        fontSize: 9,
        textColor: [35, 35, 40],
        lineColor: [230, 230, 235],
        lineWidth: 0.2,
        halign: 'center',
        valign: 'middle',
        cellPadding: { top: 3, right: 6, bottom: 3, left: 6 },
        minCellHeight: 9,
        fillColor: [255, 255, 255],
      },
      headStyles: {
        fillColor: tint,
        textColor: [pr, pg, pb],
        fontSize: 9,
        font: 'NotoArabic',
        halign: 'center',
        fontStyle: 'normal',
      },
      columnStyles: attColStyles,
      alternateRowStyles: { fillColor: [248, 249, 252] },
      tableLineWidth: 0,
      theme: 'plain',
    });

    const sectionEndY = doc.lastAutoTable.finalY;
    doc.setDrawColor(pr, pg, pb);
    doc.setLineWidth(0.5);
    doc.roundedRect(marginL, sectionStartY, tableW, sectionEndY - sectionStartY, radius, radius, 'S');
    y = sectionEndY + 6;
  }

  // =========================================================
  // FOOTER
  // =========================================================
  doc.setFillColor(248, 249, 252);
  doc.setDrawColor(220, 220, 228);
  doc.setLineWidth(0.3);
  doc.rect(0, pageHeight - 11, pageWidth, 11, 'FD');
  doc.setFontSize(7.5);
  doc.setTextColor(130, 130, 140);
  doc.text(
    munName + '  \u2022  ' + lbl('\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u0637\u0628\u0627\u0639\u0629', 'Print Date') + ': ' + printDate,
    pageWidth / 2,
    pageHeight - 4.5,
    { align: 'center' },
  );

  const fileName = `\u0637\u0644\u0628-${requestNumber || 'PDF'}.pdf`;
  const blobUrl = doc.output('bloburl');
  const pdfWindow = window.open(blobUrl, '_blank');
  if (pdfWindow) {
    pdfWindow.addEventListener('load', () => { try { pdfWindow.print(); } catch (e) {} });
  }
};
